/** @jsx jsx */
import { jsx } from '@emotion/core'
import { observer } from 'mobx-react'
import { isMobile } from 'react-device-detect'
import _ from 'lodash'
import React, { Fragment, useEffect } from 'react'

import { useOnUnmount } from '@utils/reactExt'
import useRootStore from '@store/useRootStore'
import { LogoWingIco } from '@shared/Icon/LogoIcon'
import RightContent from '@views/PrimaryLayout/Header/RightContent'
import Login from '@views/Login'
import * as Utils from '@utils/utils'
import { DUPLICATE_REQUEST_CHECK_TIME_LIMIT } from '@constants/index'
import TopNoticeBar from '@views/Notice/TopNoticeBar'
import { PAGE_TYPE } from '@interface/common'

import { LogoWrap, MainHeader } from './index.style'
import { GlobalStoreKey } from '@v2/constants/common'
import { GAME_TYPE } from '@game-type/game-market-types'
import Snowfall from 'react-snowfall'
// import seasonLogo from '@assets/images/bg/season-bg/season-logo-effect.png'

let intervalRefreshFeedSupplier = null
let fetchUserTimeOut = null
let fetchSimpleUserTimeOut = null

export interface HeaderProps {
  className?: string
}

const Header: React.FC<HeaderProps> = (props: HeaderProps) => {
  const { globalStore, authStore, matchupStore, routerStore, componentStore, joinStore, logStore } = useRootStore()
  const { visibleSideBar } = globalStore
  const { isJoinUrl } = joinStore
  const { className } = props

  const Menu = (
    <span className={`menu-ico ${isJoinUrl ? 'adv-hidden-fill-space' : ''}`}>
      <span className="menu-ico-line"/>
    </span>
  )

  useOnUnmount(() => {
    if (intervalRefreshFeedSupplier) {
      clearInterval(intervalRefreshFeedSupplier)
    }
  })

  useEffect(() => {
    if (intervalRefreshFeedSupplier) {
      clearInterval(intervalRefreshFeedSupplier)
    }
    intervalRefreshFeedSupplier = setInterval(async () => {
      await matchupStore.refreshFeedSupplierAndMarketProvider()
    }, 20 * 1000)
    if (authStore.isSignedIn) {
      refreshGiftBox().catch(console.error)
    }
  })

  const refreshGiftBox = async () => {
    await componentStore.getGiftBox({})
  }

  const refreshSliderBar = async () => {
    // NOTE : 재화갱신후에 좌측 메뉴 슬라이드 바 시작
    clearTimeout(fetchUserTimeOut)
    fetchUserTimeOut = setTimeout(async () => {
      await authStore.refreshSimpleUserInfo(true)
    }, DUPLICATE_REQUEST_CHECK_TIME_LIMIT)
  }

  const onClickSideMenuOpenButton = (event: any) => {
    if (isJoinUrl) {
      return
    }

    if (!authStore.isSignedIn) {
      authStore.showLoginPopup(<Login/>)
    } else {
      if (visibleSideBar) {
        logStore.writeButtonLog({ pageId: 'd010', buttonId: '002' }).catch(console.error)
      } else {
        logStore.writeButtonLog({ pageId: 'i010', buttonId: '001' }).catch(console.error)
      }

      if (isMobile) {
        event.preventDefault()
      }
      event.stopPropagation()

      refreshGiftBox().catch(console.error)
      refreshSliderBar().catch(console.error)

      globalStore.setVisibleSideBar(!visibleSideBar)
    }
  }

  const isMenuChecked = () => {
    return visibleSideBar
  }

  const showForefrontPopup = async () => {
    const isShow = await componentStore.fetchForefrontPopups()

    if (isShow) {
      globalStore.setVisibleForefrontPopup(true)
    } else {
      globalStore.setVisibleForefrontPopup(false)
    }
  }

  const closeMatchFilter = () => {
    globalStore.setVisibleMatchFilterPopup(false)
    matchupStore.resetTournamentFilterList()
  }

  const closeMatchupDetail = () => {
    globalStore.setVisibleMatchupDetailPopup(false)
  }

  const onClickHomeLinkAnchor = async () => {
    if (!matchupStore.isLoading) {
      if (authStore.isSignedIn) {
        clearTimeout(fetchSimpleUserTimeOut)
        fetchSimpleUserTimeOut = setTimeout(async () => {
          await authStore.refreshSimpleUserInfo()
        }, 500)
        componentStore.fetchIsPossibleInvitor().catch(err => console.error(err))
        componentStore.fetchExtraOddsItemDetails().catch(err => console.error(err))
        await showForefrontPopup()
      }
      matchupStore.cleanup()
      matchupStore.setPageType(undefined)
      matchupStore.setMatchTabInitializeMajorUniqueId(undefined)
      matchupStore.setMatchTabInitializeSportId(undefined)
      matchupStore.setCategoryTab(0, GAME_TYPE.CROSS)
      const hashParameters = Utils.getAttachableHashParameters(true)

      if (Utils.isHomePage() && _.isEmpty(matchupStore.club)) {
        const globalValue = globalStore.findGlobalSetting(GlobalStoreKey.DEFAULT_GUEST_CLUB)?.value
        const guestClubId = globalValue ?? process.env.DEFAULT_GUEST_CLUB_ID
        routerStore.push(`/matchup/cross/${guestClubId}${hashParameters}`)
        return
      }
      closeMatchFilter()
      closeMatchupDetail()
      Utils.reloadPage(`matchup/cross/${Utils.getInitializeClubId()}${hashParameters}`)
    }
  }

  const isSnowEffectEnabled
    = globalStore.findGlobalSetting(GlobalStoreKey.INGAME_SNOW_EFFECT_ENABLED)?.value === '1'

  const makeSnowParticlesComponent = (): JSX.Element => {
    const particlesNumValue = isMobile ? 11 : 24 /* 모바일 눈 갯수: 12, 피씨 눈 갯수: 24 */

    return (
      <Snowfall
        color={'rgba(255, 255, 255, 0.5)'}
        changeFrequency={30}
        radius={[0.3, 0.9]}
        speed={[-0.3, -0.01]}
        wind={[-0.8, -0.01]}
        snowflakeCount={particlesNumValue}
      />
    )
  }

  return (
    <MainHeader key="Header" id={!isMobile && 'pcMainHeader'}
                className={!isMobile ? `pc-main-header ${className}` : `${className}`}>

      {/* 눈 내리는 효과 */}
      {isSnowEffectEnabled && makeSnowParticlesComponent()}

      {!isMobile &&
        <Fragment>
          <input
            type="checkbox"
            id="menu"
            key="Header:MenuButton"
            onClick={onClickSideMenuOpenButton}
            readOnly
            // onChange={onChangeSideMenuOpenButton}
            checked={isMenuChecked()}
            className={`menu-btn ${isJoinUrl ? 'adv-hidden-fill-space' : ''}`}/>
          <label htmlFor="menu">{Menu}</label>
        </Fragment>
      }
      {/*{ 여기의 TopNoticeBar 는 스크롤을 내렸을때 상단의 프로필 이미지 좌측에 보여지게 된다. }*/}
      {authStore.isSignedIn && isMobile && Utils.isViewPage(PAGE_TYPE.MATCHUP) &&
        <TopNoticeBar className="header-notice"/>}
      <LogoWrap onClick={onClickHomeLinkAnchor} className="logo-wrap cursor-pointer">
        <span className="adv-logo">
          <LogoWingIco/>
          {/* 시즌 연출 */}
          {/*<img src={seasonLogo} alt="시즌 로고 장식" loading="lazy" */}
          {/*     className={isMobile ? 'logo-effect' : 'logo-effect pc'}/>*/}
        </span>
        {!isMobile && (<span className="adv-pc adv-txt">승부사온라인</span>)}
      </LogoWrap>
      <RightContent/>


    </MainHeader>
  )
}

export default observer(Header)
